import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, TrendingUp, Users, ArrowRight, Car, Lock, Star, Crown, CheckCircle, Clock } from 'lucide-react';
import { PaymentModal } from '../payment/PaymentModal';
import type { CarGoal } from '../../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CampaignRow {
  id: string;
  title: string;
  description: string;
  goal_amount: number;
  current_amount: number;
  status: string;
  created_at: string;
  creator_name: string;
  supporter_count: number;
  phase: 'friends' | 'investor_network' | 'algorithm';
  days_left: number;
  days_elapsed: number;
  avg_contribution: number;
  investor_count: number;
  funding_last_4h: number;
  days_until_investor_network: number;
}

interface PowerFam {
  id: string;
  title: string;
  total_raised: number;
  supporter_count: number;
  avg_contribution: number;
  creator_name: string;
}

type Tab = 'live' | 'upcoming' | 'past';

// ---------------------------------------------------------------------------
// Sample fallback data
// ---------------------------------------------------------------------------

const SAMPLE_LIVE: CampaignRow[] = [
  {
    id: 'sample-1',
    title: 'Getting My First Family Car',
    description: 'I need reliable transportation to get my kids to school and myself to work every day.',
    goal_amount: 18000, current_amount: 9800, status: 'active',
    created_at: new Date(Date.now() - 4 * 86400000).toISOString(),
    creator_name: 'Jordan T.', supporter_count: 9,
    phase: 'investor_network', days_left: 3, days_elapsed: 4,
    avg_contribution: 487, investor_count: 6, funding_last_4h: 750, days_until_investor_network: 0,
  },
  {
    id: 'sample-2',
    title: 'Tesla Model 3 for the Family',
    description: 'Saving on gas and getting something safe for long trips with the kids.',
    goal_amount: 32000, current_amount: 14500, status: 'active',
    created_at: new Date(Date.now() - 5 * 86400000).toISOString(),
    creator_name: 'Maya R.', supporter_count: 11,
    phase: 'investor_network', days_left: 2, days_elapsed: 5,
    avg_contribution: 620, investor_count: 8, funding_last_4h: 1200, days_until_investor_network: 0,
  },
];

const SAMPLE_UPCOMING: CampaignRow[] = [
  {
    id: 'sample-3',
    title: 'First Car for My Kids',
    description: 'My oldest just turned 16. I want to give them something reliable and safe.',
    goal_amount: 14000, current_amount: 3200, status: 'active',
    created_at: new Date(Date.now() - 2 * 86400000).toISOString(),
    creator_name: 'Darius M.', supporter_count: 8,
    phase: 'friends', days_left: 5, days_elapsed: 2,
    avg_contribution: 250, investor_count: 0, funding_last_4h: 0, days_until_investor_network: 1,
  },
];

const SAMPLE_PAST: CampaignRow[] = [
  {
    id: 'sample-p1',
    title: '2022 Honda Accord — Funded!',
    description: 'Community came together to get Keisha her first reliable car.',
    goal_amount: 22000, current_amount: 22000, status: 'funded',
    created_at: new Date(Date.now() - 30 * 86400000).toISOString(),
    creator_name: 'Keisha B.', supporter_count: 14,
    phase: 'algorithm', days_left: 0, days_elapsed: 30,
    avg_contribution: 530, investor_count: 9, funding_last_4h: 0, days_until_investor_network: 0,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function campaignRowToGoal(c: CampaignRow): CarGoal {
  return {
    id: c.id, user_id: '', title: c.title, description: c.description,
    goal_amount: c.goal_amount, current_amount: c.current_amount,
    image_url: '', status: c.status as CarGoal['status'],
    created_at: new Date(c.created_at), updated_at: new Date(c.created_at),
  };
}

function momentumScore(c: CampaignRow) {
  const progress = c.goal_amount > 0 ? c.current_amount / c.goal_amount : 0;
  return (
    (c.funding_last_4h / (c.goal_amount || 1)) * 0.4 +
    progress * 0.3 +
    (c.phase === 'investor_network' ? 0.3 : 0)
  );
}

// ---------------------------------------------------------------------------
// Urgency badge
// ---------------------------------------------------------------------------

function UrgencyBadge({ campaign }: { campaign: CampaignRow }) {
  if (campaign.days_left <= 2 && campaign.phase !== 'algorithm') {
    return (
      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">
        ⏰ {campaign.days_left}d left
      </span>
    );
  }
  if (campaign.investor_count >= 8) {
    return (
      <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-800 text-xs font-semibold px-2 py-0.5 rounded-full">
        <Lock className="w-3 h-3" /> Almost full
      </span>
    );
  }
  if (campaign.phase === 'friends') {
    return (
      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">
        <Clock className="w-3 h-3" /> Opens to investors in {campaign.days_until_investor_network}d
      </span>
    );
  }
  if (campaign.phase === 'investor_network') {
    return (
      <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-800 text-xs font-semibold px-2 py-0.5 rounded-full">
        <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse" />
        Open to investors · earn returns
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs font-semibold px-2 py-0.5 rounded-full">
      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
      Underwriting
    </span>
  );
}

// ---------------------------------------------------------------------------
// Featured campaign — mobile-first layout
// ---------------------------------------------------------------------------

function FeaturedCampaign({ campaign, onSupport }: { campaign: CampaignRow; onSupport: () => void }) {
  const progress = Math.min(100, Math.round((campaign.current_amount / campaign.goal_amount) * 100));
  const isTrending = campaign.funding_last_4h > campaign.goal_amount * 0.03;

  return (
    <a href={`/goal/${campaign.id}`} className="block rounded-3xl overflow-hidden shadow-xl mb-10 group">
      {/* Gradient hero — fixed height, no absolute badge collision */}
      <div className="relative h-48 sm:h-64 bg-gradient-to-br from-indigo-600 to-purple-700">
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950/80 to-transparent" />

        {/* Top badge row — single line, wraps cleanly */}
        <div className="absolute top-3 left-3 right-3 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 bg-amber-400 text-amber-900 text-xs font-bold px-2.5 py-1 rounded-full shadow">
            <Zap className="w-3 h-3" /> Featured
          </span>
          {isTrending && (
            <span className="inline-flex items-center gap-1 bg-rose-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow">
              🔥 Trending
            </span>
          )}
          {/* Phase badge pushed to same row but right-aligned on wide, wraps on narrow */}
          <span className="ml-auto">
            <UrgencyBadge campaign={campaign} />
          </span>
        </div>

        {/* Creator + title pinned to bottom of image */}
        <div className="absolute bottom-3 left-4 right-4">
          <p className="text-white/50 text-xs leading-none mb-0.5">{campaign.creator_name}</p>
          <h2 className="text-white text-xl sm:text-2xl font-extrabold leading-tight line-clamp-2">
            {campaign.title}
          </h2>
        </div>
      </div>

      {/* White card body — all content below the image */}
      <div className="bg-white px-5 pt-4 pb-5 space-y-3">
        <p className="text-gray-500 text-sm line-clamp-2">{campaign.description}</p>

        {/* Progress */}
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span className="font-bold text-gray-800 text-sm">{fmt(campaign.current_amount)}</span>
            <span>{progress}% of {fmt(campaign.goal_amount)}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-rose-400 to-amber-400 rounded-full transition-all duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Anchor copy */}
        {campaign.avg_contribution > 0 && (
          <p className="text-xs text-gray-500">
            💰 Avg contribution: <span className="font-semibold text-gray-700">{fmt(campaign.avg_contribution)}</span>
            {campaign.investor_count > 0 && (
              <> · <span className="font-semibold text-gray-700">{campaign.investor_count}</span> investors in</>
            )}
          </p>
        )}

        {/* CTA + assurance */}
        <button
          onClick={(e) => { e.preventDefault(); onSupport(); }}
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-400 hover:to-amber-400 text-white font-bold py-3 px-6 rounded-2xl text-sm transition-all duration-200 active:scale-95 shadow"
        >
          {campaign.current_amount === 0 ? 'Be the first to fund' : 'Fund this FAM'}
          <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-center text-xs text-gray-400">
          🔒 Full refund if this campaign doesn't fund by Day 7
        </p>
      </div>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Power FAM strip
// ---------------------------------------------------------------------------

function PowerFamStrip() {
  const MEDAL = ['🥇', '🥈', '🥉'];
  const EMOJIS = ['🚗', '⚡', '🚙'];
  const [powerFams, setPowerFams] = useState<PowerFam[]>([]);

  useEffect(() => {
    fetch('/api/leaderboard')
      .then((r) => r.json())
      .then((data: { powerFams?: PowerFam[] }) => {
        if (data.powerFams?.length) setPowerFams(data.powerFams.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  if (!powerFams.length) return null;

  return (
    <section className="mb-10">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-5 h-5 text-amber-500" />
        <h2 className="text-lg font-bold text-gray-900">⚡ Power FAMs this week</h2>
        <span className="ml-auto text-xs text-gray-400">Avg contribution</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {powerFams.map((fam, i) => (
          <div key={fam.id} className={`bg-white rounded-2xl border px-5 py-4 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-2 ${i === 0 ? 'border-amber-200 bg-amber-50/30' : 'border-gray-100'}`}>
            <div className="flex items-center gap-2">
              <span className="text-xl">{MEDAL[i]}</span>
              <span className="text-xl">{EMOJIS[i]}</span>
              {i === 0 && <span className="ml-auto text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Power FAM</span>}
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">{fam.creator_name?.split(' ')[0] ?? 'FAM'}</p>
              <p className="text-gray-500 text-xs truncate">{fam.title}</p>
            </div>
            <div className="mt-auto flex items-end justify-between">
              <div>
                <p className="text-indigo-600 font-bold text-lg tabular-nums">{fmt(fam.avg_contribution ?? 0)}</p>
                <p className="text-gray-400 text-xs">avg · {fam.supporter_count} supporters</p>
              </div>
              <span className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full">{fmt(fam.total_raised)} raised</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Status preview (badge unlock callout)
// ---------------------------------------------------------------------------

function StatusPreview() {
  return (
    <div className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl px-5 py-4">
      <p className="text-xs font-semibold text-indigo-400 uppercase tracking-wide mb-3">What you unlock</p>
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span>🏅</span>
          <span className="text-gray-700">First contribution → <span className="font-bold text-gray-900">Day One</span> badge</span>
        </div>
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <span className="text-gray-700"><span className="font-bold text-gray-900">$100+</span> → FAM Star</span>
        </div>
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-purple-500 flex-shrink-0" />
          <span className="text-gray-700">Invest → <span className="font-bold text-gray-900">token returns</span> + co-ownership</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campaign card (grid)
// ---------------------------------------------------------------------------

function CampaignCard({ campaign, onSupport }: { campaign: CampaignRow; onSupport: (g: CarGoal) => void }) {
  const progress = Math.min(100, Math.round((campaign.current_amount / campaign.goal_amount) * 100));
  const isTrending = campaign.funding_last_4h > campaign.goal_amount * 0.03;
  const isPast = ['funded', 'completed', 'expired'].includes(campaign.status);

  return (
    <a href={`/goal/${campaign.id}`} className="block group">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
        {/* Hero */}
        <div className="relative h-32 bg-gradient-to-br from-indigo-500 to-purple-600">
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          {isTrending && !isPast && (
            <span className="absolute top-2 right-2 bg-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              🔥 Trending
            </span>
          )}
          {isPast && (
            <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              <CheckCircle className="w-3 h-3" /> Funded
            </span>
          )}
          <div className="absolute bottom-2 left-3 right-3">
            <p className="text-white/60 text-xs">{campaign.creator_name}</p>
            <p className="text-white font-bold text-sm leading-tight line-clamp-1">{campaign.title}</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 pt-3 pb-4 space-y-2.5">
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span className="font-semibold text-gray-800">{fmt(campaign.current_amount)}</span>
              <span>{progress}% of {fmt(campaign.goal_amount)}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-rose-400 to-amber-400 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {campaign.avg_contribution > 0 ? (
            <p className="text-xs text-gray-500">
              💰 Avg: <span className="font-semibold text-gray-700">{fmt(campaign.avg_contribution)}</span>
              {campaign.investor_count > 0 && <> · <span className="font-semibold">{campaign.investor_count}</span> investors</>}
            </p>
          ) : (
            <p className="text-xs text-gray-400">Be the first to contribute</p>
          )}

          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <UrgencyBadge campaign={campaign} />
            </div>
            {!isPast && (
              <button
                onClick={(e) => { e.preventDefault(); onSupport(campaignRowToGoal(campaign)); }}
                className="flex-shrink-0 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                Fund →
              </button>
            )}
          </div>
        </div>
      </div>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Upcoming campaign card (friends phase — no Fund CTA)
// ---------------------------------------------------------------------------

function UpcomingCard({ campaign }: { campaign: CampaignRow }) {
  const progress = Math.min(100, Math.round((campaign.current_amount / campaign.goal_amount) * 100));

  return (
    <a href={`/goal/${campaign.id}`} className="block group">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
        <div className="relative h-32 bg-gradient-to-br from-slate-500 to-slate-700">
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute top-2 right-2">
            <span className="inline-flex items-center gap-1 bg-amber-400 text-amber-900 text-xs font-bold px-2 py-0.5 rounded-full">
              <Clock className="w-3 h-3" /> Opens in {campaign.days_until_investor_network}d
            </span>
          </div>
          <div className="absolute bottom-2 left-3 right-3">
            <p className="text-white/60 text-xs">{campaign.creator_name}</p>
            <p className="text-white font-bold text-sm leading-tight line-clamp-1">{campaign.title}</p>
          </div>
        </div>
        <div className="px-4 pt-3 pb-4 space-y-2.5">
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span className="font-semibold text-gray-800">{fmt(campaign.current_amount)}</span>
              <span>{progress}% of {fmt(campaign.goal_amount)}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-slate-300 to-slate-400 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <p className="text-xs text-gray-400">
            {campaign.supporter_count} supporters so far · funding in friends-only phase
          </p>
        </div>
      </div>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

function TabBar({ active, counts, onChange }: { active: Tab; counts: Record<Tab, number>; onChange: (t: Tab) => void }) {
  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'live', label: 'Live', icon: <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse inline-block" /> },
    { key: 'upcoming', label: 'Upcoming', icon: <Clock className="w-3.5 h-3.5" /> },
    { key: 'past', label: 'Past', icon: <CheckCircle className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-8">
      {tabs.map(({ key, label, icon }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-150 ${
            active === key
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {icon}
          {label}
          {counts[key] > 0 && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${active === key ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-gray-500'}`}>
              {counts[key]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function CampaignsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('live');
  const [liveCampaigns, setLiveCampaigns] = useState<CampaignRow[]>([]);
  const [upcomingCampaigns, setUpcomingCampaigns] = useState<CampaignRow[]>([]);
  const [pastCampaigns, setPastCampaigns] = useState<CampaignRow[]>([]);
  const [loadingTab, setLoadingTab] = useState<Tab | null>('live');
  const [paymentGoal, setPaymentGoal] = useState<CarGoal | null>(null);

  // Fetch live + upcoming on mount (single request returns both)
  useEffect(() => {
    setLoadingTab('live');
    fetch('/api/campaigns-public?tab=live')
      .then((r) => r.json())
      .then((data: { campaigns?: CampaignRow[]; upcomingCampaigns?: CampaignRow[] }) => {
        const live = data.campaigns ?? [];
        const upcoming = data.upcomingCampaigns ?? [];
        setLiveCampaigns(live.length > 0 ? live : SAMPLE_LIVE);
        setUpcomingCampaigns(upcoming.length > 0 ? upcoming : SAMPLE_UPCOMING);
      })
      .catch(() => {
        setLiveCampaigns(SAMPLE_LIVE);
        setUpcomingCampaigns(SAMPLE_UPCOMING);
      })
      .finally(() => setLoadingTab(null));
  }, []);

  // Fetch past lazily when user clicks that tab
  useEffect(() => {
    if (activeTab !== 'past' || pastCampaigns.length > 0) return;
    setLoadingTab('past');
    fetch('/api/campaigns-public?tab=past')
      .then((r) => r.json())
      .then((data: { campaigns?: CampaignRow[] }) => {
        const rows = data.campaigns ?? [];
        setPastCampaigns(rows.length > 0 ? rows : SAMPLE_PAST);
      })
      .catch(() => setPastCampaigns(SAMPLE_PAST))
      .finally(() => setLoadingTab(null));
  }, [activeTab, pastCampaigns.length]);

  // Sort live: investor_network first → 40-80% zone → velocity
  const phaseOrder: Record<string, number> = { investor_network: 0, algorithm: 1, friends: 2 };
  const sortedLive = [...liveCampaigns].sort((a, b) => {
    if (a.phase !== b.phase) return (phaseOrder[a.phase] ?? 3) - (phaseOrder[b.phase] ?? 3);
    const aR = a.goal_amount > 0 ? a.current_amount / a.goal_amount : 0;
    const bR = b.goal_amount > 0 ? b.current_amount / b.goal_amount : 0;
    const aZ = aR >= 0.4 && aR <= 0.8 ? 1 : 0;
    const bZ = bR >= 0.4 && bR <= 0.8 ? 1 : 0;
    if (aZ !== bZ) return bZ - aZ;
    return b.funding_last_4h - a.funding_last_4h;
  });

  const featuredCampaign = [...sortedLive].sort((a, b) => momentumScore(b) - momentumScore(a))[0] ?? null;
  const restLive = sortedLive.filter((c) => c.id !== featuredCampaign?.id);

  // Stats
  const totalSupporters = liveCampaigns.reduce((s, c) => s + c.supporter_count, 0);
  const withAvg = liveCampaigns.filter((c) => c.avg_contribution > 0);
  const overallAvg = withAvg.length > 0
    ? Math.round(withAvg.reduce((s, c) => s + c.avg_contribution, 0) / withAvg.length)
    : 0;

  const isLoading = loadingTab !== null;

  const tabCounts: Record<Tab, number> = {
    live: liveCampaigns.length,
    upcoming: upcomingCampaigns.length,
    past: pastCampaigns.length,
  };

  return (
    <main className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/')} className="text-sm text-gray-400 hover:text-gray-600 transition-colors mb-2 block">
          ← Home
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Campaigns</h1>
            <p className="text-gray-500 mt-0.5 text-sm">Real people. Real goals. Fund your FAM.</p>
          </div>
          {!isLoading && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500 flex-shrink-0 mt-1">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              {liveCampaigns.length} live
            </div>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <TabBar active={activeTab} counts={tabCounts} onChange={setActiveTab} />

      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-4 animate-pulse">
          <div className="h-72 bg-gray-200 rounded-3xl" />
          <div className="grid grid-cols-2 gap-4">
            {[1, 2].map(i => <div key={i} className="h-48 bg-gray-100 rounded-2xl" />)}
          </div>
        </div>
      )}

      {/* LIVE TAB */}
      {!isLoading && activeTab === 'live' && (
        <>
          {featuredCampaign && (
            <FeaturedCampaign
              campaign={featuredCampaign}
              onSupport={() => setPaymentGoal(campaignRowToGoal(featuredCampaign))}
            />
          )}

          <PowerFamStrip />
          <StatusPreview />

          {/* Stats bar */}
          <div className="flex flex-wrap gap-4 mb-8 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm text-sm">
            <div className="flex items-center gap-2">
              <Car className="w-4 h-4 text-rose-400" />
              <span className="text-gray-600">⚡ <span className="font-bold text-gray-900">{liveCampaigns.length}</span> active</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-400" />
              <span className="text-gray-600">Join <span className="font-bold text-gray-900">{totalSupporters}</span> supporters</span>
            </div>
            {overallAvg > 0 && (
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span className="text-gray-600">Avg: <span className="font-bold text-gray-900">{fmt(overallAvg)}</span></span>
              </div>
            )}
          </div>

          {restLive.length > 0 && (
            <section>
              <h2 className="text-lg font-bold text-gray-900 mb-4">All Live Campaigns</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {restLive.map((c) => (
                  <CampaignCard key={c.id} campaign={c} onSupport={setPaymentGoal} />
                ))}
              </div>
            </section>
          )}

          {liveCampaigns.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <Car className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No live campaigns right now</p>
              <p className="text-sm mt-1">Check back soon — new FAM links go live daily.</p>
            </div>
          )}
        </>
      )}

      {/* UPCOMING TAB */}
      {!isLoading && activeTab === 'upcoming' && (
        <>
          <div className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4 mb-6 text-sm text-amber-800">
            <p className="font-semibold mb-0.5">Friends-only phase</p>
            <p className="text-amber-700/80">These campaigns are currently being funded by the creator's personal network. They'll open to the FAM investor network soon — check back or save the link.</p>
          </div>

          {upcomingCampaigns.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {upcomingCampaigns.map((c) => <UpcomingCard key={c.id} campaign={c} />)}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400">
              <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No upcoming campaigns right now</p>
              <p className="text-sm mt-1">New campaigns will appear here before they open to investors.</p>
            </div>
          )}
        </>
      )}

      {/* PAST TAB */}
      {!isLoading && activeTab === 'past' && (
        <>
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-5 py-4 mb-6 text-sm text-emerald-800">
            <p className="font-semibold mb-0.5">Funded campaigns</p>
            <p className="text-emerald-700/80">These campaigns successfully funded. Co-ownership agreements are active and creators are making monthly payments.</p>
          </div>

          {pastCampaigns.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {pastCampaigns.map((c) => <CampaignCard key={c.id} campaign={c} onSupport={setPaymentGoal} />)}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400">
              <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No completed campaigns yet</p>
              <p className="text-sm mt-1">The first funded campaigns will appear here.</p>
            </div>
          )}
        </>
      )}

      {/* Payment modal */}
      {paymentGoal && (
        <PaymentModal
          isOpen={true}
          onClose={() => setPaymentGoal(null)}
          goal={paymentGoal}
          onSuccess={() => setPaymentGoal(null)}
        />
      )}
    </main>
  );
}
